import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useMemo, useState } from 'react';
import { House } from '@phosphor-icons/react';
import {
  ADMIN_GROUPS,
  ADMIN_NAV_ITEMS,
  canAccessAdminTab,
  getGroupFromPath,
} from '../utils/adminNavConfig';
import type { AdminGroup } from '../utils/adminNavConfig';
import { ADMIN_COLORS, triggerHapticFeedback } from '../utils/adminAnimations';
import { NeonBorder } from '@/components/layout/NeonLoadingLine';

const ACCENT_ACTIVE = 'var(--admin-nav-active-text)';
const GLASS_BORDER = 'var(--admin-nav-border)';
const MUTED = 'var(--admin-nav-text)';

/**
 * AdminBottomNav
 * Mobile-only bottom navigation for admin pages.
 * Mirrors the desktop sidebar: 4 group buttons + HOME.
 */
export function AdminBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { permissions } = usePermissions();
  const [showHomeConfirm, setShowHomeConfirm] = useState(false);

  const activeGroup = useMemo(
    () => getGroupFromPath(location.pathname) ?? 'oversight',
    [location.pathname],
  );

  const visibleItems = useMemo(
    () => ADMIN_NAV_ITEMS.filter(item => canAccessAdminTab(item.tabId, permissions)),
    [permissions],
  );

  const visibleGroups = useMemo(
    () => ADMIN_GROUPS.filter(g => visibleItems.some(item => item.group === g.id)),
    [visibleItems],
  );

  const handleGroupClick = (groupId: AdminGroup) => {
    triggerHapticFeedback();
    const firstItem = visibleItems.find(i => i.group === groupId);
    if (firstItem) navigate(firstItem.path);
  };

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-50 border-t"
      style={{
        backgroundColor: 'var(--admin-nav-bg)',
        borderColor: 'var(--admin-nav-border)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      }}
    >
      <NeonBorder variant="admin" />
      <div className="flex justify-evenly">
        {visibleGroups.map((group) => {
          const Icon = group.icon;
          const active = activeGroup === group.id;

          return (
            <button
              key={group.id}
              className="flex items-center justify-center h-12 w-14"
              style={{
                color: active ? ACCENT_ACTIVE : MUTED,
                backgroundColor: active ? 'var(--admin-nav-active-bg)' : undefined,
              }}
              onClick={() => handleGroupClick(group.id)}
              aria-current={active ? 'page' : undefined}
              aria-label={t(group.labelKey)}
            >
              <Icon size={28} strokeWidth={active ? 2.5 : 1.8} />
            </button>
          );
        })}

        {/* HOME - opens confirmation modal */}
        <button
          className="flex items-center justify-center h-12 w-14"
          style={{
            color: MUTED,
          }}
          onClick={() => {
            triggerHapticFeedback();
            setShowHomeConfirm(true);
          }}
          aria-label={t('nav.switchToHome')}
        >
          <House size={28} weight="regular" />
        </button>
      </div>

      {/* "Switch Back to Home?" confirmation modal */}
      {showHomeConfirm && (
        <>
          <div
            className="fixed inset-0 z-[60] bg-black/50"
            onClick={() => setShowHomeConfirm(false)}
          />
          <div
            className="fixed z-[70] left-4 right-4 bottom-[90px] flex flex-col items-center gap-4 rounded-xl px-6 py-6 bg-white border border-[#CED0D4] shadow-lg"
          >
            <House size={32} weight="regular" style={{ color: ACCENT_ACTIVE }} />
            <span
              style={{
                fontSize: '14px',
                fontWeight: 700,
                color: '#1e293b',
                textAlign: 'center',
              }}
            >
              {t('adminNav.switchHomeTitle')}
            </span>
            <div className="flex gap-3 w-full">
              <button
                className="flex-1 py-3 rounded-lg font-bold uppercase text-xs transition-all active:translate-y-px"
                style={{
                  backgroundColor: ACCENT_ACTIVE,
                  color: '#ffffff',
                  border: `1px solid ${GLASS_BORDER}`,
                  boxShadow: ADMIN_COLORS.shadow,
                }}
                onClick={() => {
                  setShowHomeConfirm(false);
                  navigate('/home');
                }}
              >
                {t('adminNav.switchHomeConfirm')}
              </button>
              <button
                className="flex-1 py-3 rounded-lg font-bold uppercase text-xs transition-all active:translate-y-px"
                style={{
                  backgroundColor: 'transparent',
                  color: MUTED,
                  border: `1px solid ${MUTED}`,
                }}
                onClick={() => setShowHomeConfirm(false)}
              >
                {t('adminNav.switchHomeCancel')}
              </button>
            </div>
          </div>
        </>
      )}
    </nav>
  );
}
